import { z } from 'zod';
import { RecordAccount, Timestamp, Uuid } from './common.js';
import { FamilyRelation } from './family.js';
import { Fact } from './facts.js';
import { NetworkNodeKind, NetworkResponse } from './network.js';
import { OrgRelation, SubjectStatus } from './vocab.js';

/**
 * **The knowledge base** — one page that answers "what does the system know
 * about X, and who does X touch" (knowledge-base design note, 2026-09-07).
 *
 * Two reads and, since 2026-09-09, exactly one write: a search over every entity
 * of the org, a panel for one of them, and a **link written by hand** between an
 * organisation or an event and another record
 * ({@link KnowledgeLinkBody}, organisation-profile note §1.1). There is still no
 * detail route and no page two, and the rest of §1.7 stands: merge, rename,
 * delete and "research this organisation" remain deliberately absent.
 *
 * **Why the kind vocabulary is the network's.** A knowledge-base row is a node
 * of the same world the graph draws, and `person | org | event` is already
 * spelled once in `network.ts` against the `entity.kind` column. Aliased rather
 * than re-enumerated so the two can never drift apart into two vocabularies
 * meaning the same three things.
 */
export const KnowledgeKind = NetworkNodeKind;
export type KnowledgeKind = z.infer<typeof KnowledgeKind>;

/**
 * The answers to "where did this come from", **highest precedence first**.
 *
 * **`split` is last before `unknown`, and that placement is the rule** (couple
 * repair, operator-tools.md §17). `entity:split-couple` mints the second half of
 * a couple cell — "Erica Malbon", out of a record that said "Stephen & Erica
 * Malbon" — and that entity carries no `import_row`, no run and no prose, so it
 * read as `unknown`: *an entity nothing on record explains*, about a person the
 * office had itself just created and could name the decision for. The operator's
 * split is the weakest of the real answers, though, not the strongest: a
 * promoted node that research turned up came to be on record because research
 * turned it up, and the split is what happened to it afterwards. So it explains
 * an entity only when nothing else does, which is what "last before unknown"
 * buys.
 */
export const KNOWLEDGE_SOURCE_KINDS = [
  'import',
  'research',
  'note',
  'intake',
  'split',
  'unknown',
] as const;
export const KnowledgeSourceKind = z.enum(KNOWLEDGE_SOURCE_KINDS);
export type KnowledgeSourceKind = z.infer<typeof KnowledgeSourceKind>;

/**
 * **Where a row came from**, in one phrase — the last field of the row (§1.3).
 *
 * The precedence is the order of {@link KNOWLEDGE_SOURCE_KINDS} and it is a
 * precedence rather than a history: an entity an import wrote and a run then
 * corroborated says `import`, because the question the row answers is how this
 * came to be on record. The whole history is the panel's `provenance`.
 *
 * `label` names the *thing*, and what that is depends on the kind: the file's
 * name for `import`, the researched person's name for `research`. The three
 * kinds that have no specific referent carry the phrase itself — "a note", "the
 * add box", "unknown" — because there is nothing else for them to name and a
 * client should not have to hold a fourth vocabulary to print them.
 */
export const KnowledgeSource = z
  .object({
    kind: KnowledgeSourceKind,
    label: z.string(),
  })
  .strict();
export type KnowledgeSource = z.infer<typeof KnowledgeSource>;

/**
 * **One row of the knowledge base**, and the same shape wherever an entity is
 * listed: a search result, one of the ten most connected, and one of the
 * entities on the panel's hop.
 *
 * `tracked` is the tracked/known distinction the People list carries as `level`
 * (subjects.ts) said as a nullable object instead of an enum, because at this
 * level the two are not symmetric: tracked *has* something known does not — a
 * `subject_record` and, for a person, a status from the org's vertical. A null
 * is a known entity, and every event is one by construction.
 *
 * **`status` inside it is nullable, and an organisation is why**
 * (organisations-as-subjects note §1 R1, §2 chunk 2a "As grounded" 1). Tracking
 * an organisation writes the same vertical-agnostic `subject_record` row a
 * person gets and no status row at all, because neither vertical's vocabulary
 * has a word for an organisation. So the two facts this object used to conflate
 * come apart: `tracked` non-null says the office keeps a record, and `status`
 * says what that record's standing is called — which only a person has. A
 * client renders "Tracked" alone for the null, which is the same fallback it
 * already had for a vertical whose words it does not know.
 *
 * The alternative was leaving `tracked` null for a tracked organisation, and it
 * is a lie a page acts on: Track would be offered on a record that already has
 * one, and Open record would be withheld from a record that exists.
 *
 * `display_name` is not nullable, unlike the graph's node. A node keeps its
 * place on the canvas after a shred and loses only its name (§8); a
 * knowledge-base row *is* the entity, so a deleted person is absent from this
 * read entirely rather than listed namelessly.
 *
 * `connections` is `entity.connection_count`: how many distinct active
 * edge-bearing fact lineages touch this entity. Lineages and not neighbours —
 * a person who is both an employee and a board member of one company is two
 * connections — because that is the number the fact write path can keep true
 * without a second read (§1.5).
 *
 * `updated_at` is the entity's, which is what "last updated" has meant on the
 * People list since known people joined it.
 */
export const KnowledgeEntry = z
  .object({
    entity_id: Uuid,
    /**
     * **The protection account this record belongs to**, and `null` in every
     * vertical that has none (security fold, 2026-09-22). Required and
     * `.nullable()` rather than `.optional()`, which is this package's rule for
     * a field that can be "none" — and which the field it replaces
     * (`account_label`, an optional string written by the removed security
     * reader) got wrong in both directions: absent meant two different things,
     * and a label with no id gave the page nothing to filter by.
     */
    account: RecordAccount.nullable(),
    kind: KnowledgeKind,
    display_name: z.string(),
    tracked: z.object({ status: SubjectStatus.nullable() }).strict().nullable(),
    connections: z.number().int().nonnegative(),
    source: KnowledgeSource,
    updated_at: Timestamp,
  })
  .strict();
export type KnowledgeEntry = z.infer<typeof KnowledgeEntry>;

/**
 * A repeatable query parameter, as one arrives on the wire: `?kind=org` gives a
 * bare string and `?kind=org&kind=event` gives an array, and the schema must not
 * care which the caller sent. The same helper `subjects.ts` uses for `status`,
 * repeated here rather than exported from there — it is three lines of wire
 * handling, and neither file should have to import the other's internals.
 */
const repeatable = (value: unknown): unknown =>
  value === undefined || Array.isArray(value) ? value : [value];

/**
 * `GET /orgs/:orgId/knowledge` — the search.
 *
 * `q` is trimmed and capped at 120 characters: it is a name, and a request
 * longer than the longest name anybody holds is a mistake rather than a query.
 * An **absent or empty** `q` is not an error and not a request for everything —
 * it is the page as it opens, and what comes back is `most_connected` with no
 * `entries` at all (§1.1: "Nothing else is listed unprompted").
 *
 * `kind` is repeatable — the three chips are a multi-select — and narrows both
 * the results and the most-connected lists.
 *
 * `limit` caps at fifty, which is also the default, because fifty *is* the cap
 * the page is designed around: past it the page says to narrow the search
 * rather than offering a page two.
 */
export const KnowledgePanelQuery = z.object({ account_id: Uuid.optional() }).strict();
export type KnowledgePanelQuery = z.infer<typeof KnowledgePanelQuery>;

export const KnowledgeSearchQuery = z
  .object({
    account_id: Uuid.optional(),
    q: z.string().trim().max(120).optional(),
    kind: z.preprocess(repeatable, z.array(KnowledgeKind).min(1)).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(50),
  })
  .strict();
export type KnowledgeSearchQuery = z.infer<typeof KnowledgeSearchQuery>;
export type KnowledgeSearchQueryInput = z.input<typeof KnowledgeSearchQuery>;

/**
 * **The ten most connected of each kind** — the one bounded list the page shows
 * before it is asked anything (§1.1).
 *
 * All three keys are always present, and a kind the `kind` filter excluded comes
 * back as an empty array rather than a missing key: a client renders three
 * sections and an absent key would be a fourth case for it to hold.
 */
export const KnowledgeMostConnected = z
  .object({
    person: z.array(KnowledgeEntry),
    org: z.array(KnowledgeEntry),
    event: z.array(KnowledgeEntry),
  })
  .strict();
export type KnowledgeMostConnected = z.infer<typeof KnowledgeMostConnected>;

/** How many of each kind the opening list carries. */
export const KNOWLEDGE_MOST_CONNECTED_PER_KIND = 10;

/**
 * The search's answer.
 *
 * `capped` is the page's own sentence — "there are more; narrow the search" —
 * and it is true when the org holds more matches than `limit`, never merely
 * when the page came back full.
 *
 * `most_connected` is **null whenever `q` said something**, which is the honest
 * shape: the opening list and a search are two different answers to two
 * different questions, and a search that also carried the openers would leave a
 * client guessing which of the two it was looking at.
 */
export const KnowledgeSearchResponse = z
  .object({
    entries: z.array(KnowledgeEntry),
    capped: z.boolean(),
    most_connected: KnowledgeMostConnected.nullable(),
  })
  .strict();
export type KnowledgeSearchResponse = z.infer<typeof KnowledgeSearchResponse>;

/**
 * The five things that put an entity on record, as the panel lists them.
 *
 * **`split` is the only one of these that is not a document, a run or a piece
 * of prose** (couple repair, operator-tools.md §17). Nothing was captured and
 * nobody wrote anything: an operator at a terminal said that one record was two
 * people, and for the half that record became there is no other line to draw.
 * Its `href_id` is the `entity_split` decision, which is where the whole of what
 * happened is written down.
 *
 * **`link` is prose the office never wrote** (2026-09-10). A link made by hand
 * — the knowledge base's own door, and the merge card's "they are related"
 * answer — files a `prose_input` row to cite, because every claim on this system
 * cites either a captured document or a piece of the office's own writing and
 * there is no document behind a button. That row is a `note` in the table and
 * was a note on this line too, so the panel told the reader "a note" about a
 * sentence nobody typed. It is its own kind here so the line can say what the
 * office actually did.
 */
export const PROVENANCE_KINDS = ['import', 'run', 'note', 'intake', 'link', 'split'] as const;
export const ProvenanceKind = z.enum(PROVENANCE_KINDS);
export type ProvenanceKind = z.infer<typeof ProvenanceKind>;

/**
 * **One line of "where it came from"** (§1.4) — a file, a run, a piece of
 * first-party prose.
 *
 * `href_id` is the id of that thing in its own plane: an `import.id`, a
 * `run.id`, a `prose_input.id`. It is deliberately not a URL — where a session
 * page or a run lives is the dashboard's routing to decide, and a server that
 * wrote paths here would be the second place that decision lived.
 *
 * `label` follows the same rule {@link KnowledgeSource} states: the file's name
 * for an import, the run's kind for a run, and the phrase itself for the kinds
 * of prose that have nothing else to name.
 *
 * **A `link` has an `href_id` and no page**, which is not a contradiction: the
 * id is the `prose_input.id` behind the fact, kept because it identifies the
 * line, and the client renders the line as plain text because a sentence the
 * office assembled out of two picks has no screen of its own to open.
 */
export const ProvenanceEntry = z
  .object({
    kind: ProvenanceKind,
    label: z.string(),
    at: Timestamp,
    href_id: Uuid,
  })
  .strict();
export type ProvenanceEntry = z.infer<typeof ProvenanceEntry>;

/** How many provenance lines the panel carries, newest first. */
export const KNOWLEDGE_PROVENANCE_LIMIT = 20;
/** How many facts the panel carries before it says there are more. */
export const KNOWLEDGE_FACTS_LIMIT = 200;

/**
 * `GET /orgs/:orgId/knowledge/:entityId` — the panel, in the order §1.4 reads
 * it: the head, who it touches, the graph, what we know, where it came from.
 *
 * `touches` is the people, organisations and events on the **same single hop**
 * the graph draws, as rows rather than as circles — tracked people first, then
 * by connections. It is derived from `network` and costs nothing extra: the hop
 * has already been read.
 *
 * `network` is the record's own network payload with the panel's entity as
 * centre. `subject_entity_id` names that centre and `is_subject` marks it, for
 * any kind of entity — on the subject route those two are the subject, which is
 * why the field is spelled that way and why the route's payload is unchanged.
 *
 * `facts` are the active facts with this entity in either column, projected the
 * way the record and the brief project them, so the dashboard has one fact
 * renderer here too. They are capped at {@link KNOWLEDGE_FACTS_LIMIT} and
 * `facts_capped` says the cap bit — a panel is a read of a page, not of a
 * dossier, and an entity with more facts than that is one to open the record
 * for.
 */
export const KnowledgePanel = z
  .object({
    entry: KnowledgeEntry,
    touches: z.array(KnowledgeEntry),
    network: NetworkResponse,
    facts: z.array(Fact),
    // Reviewed statements that do not have canonical fact lineages.
    observations: z
      .array(z.object({ id: Uuid, statement: z.string(), basis: z.string() }).strict())
      .optional(),
    facts_capped: z.boolean(),
    provenance: z.array(ProvenanceEntry),
  })
  .strict();
export type KnowledgePanel = z.infer<typeof KnowledgePanel>;

/* ————————————————————————— the one write (§1.7, revised) ————————————————————————— */

/**
 * **Which of the fact's two slots the page's own record sits in.**
 *
 * `POST /orgs/:orgId/knowledge/:entityId/links` is asked from *one* record's
 * page, so the path already names half of the pair. What the body has to add is
 * the other half and which way round the two of them go — and that is one word
 * rather than two ids, because the two ids are not free: one of them is always
 * the record whose page the office is standing on, and a body that named both
 * could name neither.
 *
 * The words are the `org_relation` columns themselves (`fact-types.ts`), so
 * nothing has to be translated on the way in: `subject` puts this record in the
 * fact's `org` slot — the smaller or governed side, or the host — and
 * `counterparty` puts it in the other one — the whole, the governing body, or
 * the event that was hosted.
 *
 * An event's page can only ever send `counterparty`: the host is always an
 * organisation, so "this gala was hosted by that club" is the only sentence
 * there is to write from an event. The server enforces that against the
 * catalog's slot rule rather than trusting the word.
 */
export const KNOWLEDGE_LINK_DIRECTIONS = ['subject', 'counterparty'] as const;
export const KnowledgeLinkDirection = z.enum(KNOWLEDGE_LINK_DIRECTIONS);
export type KnowledgeLinkDirection = z.infer<typeof KnowledgeLinkDirection>;

/**
 * **A link written by hand** (organisation-profile note §1.1) — the office
 * saying, of two records it is looking at, that one stands inside, under,
 * beside, or as the host of the other.
 *
 * `relation` is the catalog's own four words ({@link OrgRelation}) and not the
 * merge card's three: this door is reached from an organisation's page *and*
 * from an event's, and `hosted` — "medi.ci hosted Medici Day 2026" — is the
 * whole reason an event has this door at all.
 *
 * Every field is required. There is no partial link: a body missing the
 * direction would be asking the server to guess which of two organisations is
 * the whole, which is the one thing this gesture exists to state.
 */
export const KnowledgeLinkBody = z
  .object({
    /** The other record, picked out of the knowledge base's own search. */
    other_entity_id: Uuid,
    relation: OrgRelation,
    direction: KnowledgeLinkDirection,
  })
  .strict();
export type KnowledgeLinkBody = z.infer<typeof KnowledgeLinkBody>;

/**
 * What the link came back as.
 *
 * `lineage_id` is the fact the link now lives on — the same lineage every other
 * reader of that fact knows it by, so a client that wants to point at it can.
 * It is an id on the wire and never a word on a screen: the record shows names.
 *
 * `sentence` is what was recorded, in the office's own words, handed back so the
 * page can say what it wrote rather than saying "done" — and it names the two
 * records **in the order the page named them**, which is the order the modal
 * showed before the press.
 *
 * That is not always the order the fact is stored in. `affiliated_with` has two
 * slots that do not rank, so the record folds the pair onto one arbitrary order
 * (the server's rule, not the client's) and the fact's `detail` is written that
 * way, whichever page the office was standing on. The answer is not: a reader who
 * pressed a button under "A and B are affiliated" is told that sentence back, and
 * the same tie asked from B's page says "B and A are affiliated". One fact, one
 * lineage, two readings of it — so a client must not treat this string as the
 * fact's `detail`, only as what it just said.
 *
 * `already_recorded` is the honest answer to pressing the button twice. The link
 * is deduped on the catalog's key (the pair plus the relation), so a repeat
 * lands on the *same* lineage and writes no second fact; the call still answers
 * 200, because the office asked for a state and the record is in it. Nothing
 * here is nullable: all three are always known by the time there is a response
 * at all.
 */
export const KnowledgeLinkResponse = z
  .object({
    lineage_id: Uuid,
    sentence: z.string(),
    already_recorded: z.boolean(),
  })
  .strict();
export type KnowledgeLinkResponse = z.infer<typeof KnowledgeLinkResponse>;

/**
 * **A family tie written by hand** (fix-a-fact note, 2026-09-30) — the office
 * saying, of two people it has on file, how they are related:
 * `POST /orgs/:orgId/knowledge/:entityId/family`.
 *
 * `relation` is what the **other** record is to this one — "Ada Moreno is Lew
 * Price's grandparent" from Lew's page — so the fact is written with the other
 * person as `a` and this one as `b` (the catalog reads "`a` is the {relation} of
 * `b`"). Both must be people, and different ones.
 *
 * It answers {@link KnowledgeLinkResponse}: the tie's lineage, the sentence, and
 * whether the same tie was already on the record.
 */
export const KnowledgeFamilyLinkBody = z
  .object({
    other_entity_id: Uuid,
    relation: FamilyRelation,
  })
  .strict();
export type KnowledgeFamilyLinkBody = z.infer<typeof KnowledgeFamilyLinkBody>;

/* ————————————————————————— rename by hand ————————————————————————— */

/**
 * **How long a name may be.** The create door
 * (`subjects.ts`, `CreateSubjectBody.name`) states no ceiling at all, and
 * `entity.display_name` is plain `text`, so "the create door's max" resolves to
 * *nothing* — which is not a bound a write door should carry. A record whose
 * name is a paragraph is not a record anybody can read: it is what a paste into
 * the wrong box looks like, and it lands in every list, every graph label and
 * every sentence this system writes.
 *
 * Two hundred characters is well past the longest real name the record holds
 * (the longest on prod is under seventy) and well short of a paste. It is
 * applied to the **rename** door only, deliberately: widening the create door is
 * a behaviour change to a door this chunk was not asked to touch, and it would
 * newly refuse names the record may already be holding.
 */
export const DISPLAY_NAME_MAX_LENGTH = 200;
export const DISPLAY_NAME_TOO_LONG = 'That name is too long.';

/**
 * **Rename this record** — `POST /orgs/:orgId/knowledge/:entityId/rename`
 * (organisations-as-subjects note §1 R5/R6, §2 chunk 1).
 *
 * The knowledge base is the every-kind surface — organisations, events and
 * tracked people are all entries on it — so one door serves the organisation
 * page, the event panel and the person record's header. R6 is why: the four
 * early merges that kept the shorter spelling include a person (Andrew Paul),
 * and a rename that only organisations could reach would not reach him.
 *
 * One field, and it is the whole gesture. There is no "and leave a redirect"
 * switch: the old spelling **always** stays resolvable where there is anything
 * to hold (see {@link KnowledgeRenameResponse}), because a rename that stopped
 * the old name finding the record is how the next claim that uses it mints a
 * second one.
 */
export const KnowledgeRenameBody = z
  .object({
    name: z.string().trim().min(1).max(DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_TOO_LONG),
  })
  .strict();
export type KnowledgeRenameBody = z.infer<typeof KnowledgeRenameBody>;

/**
 * What the rename came back as — in names, never ids, because this is what the
 * modal says back to the reader.
 *
 * `unchanged` is the honest answer to typing the name the record already has:
 * 200, nothing filed, no decision, no ledger row and no stub — the same shape
 * `already_recorded` gives the link pressed twice. It is **not** true of a
 * change of case or punctuation ("T Dupree Scovell" → "T. Dupree Scovell"),
 * which really does move `display_name` and really is filed; that one simply
 * leaves no stub behind, because a stub there would carry the record's own
 * normalized name and answer to a name the record already answers to.
 *
 * `sentence` is the one line the office reads. It gains its second clause — "The
 * old name still finds it." — exactly when a redirect was written, so the reader
 * is told the thing they would otherwise have to take on trust, and is not told
 * it on the two occasions it would be a promise about a row that does not exist.
 *
 * Nothing here is nullable: all four are known by the time there is a response
 * at all.
 */
export const KnowledgeRenameResponse = z
  .object({
    from_name: z.string(),
    to_name: z.string(),
    unchanged: z.boolean(),
    sentence: z.string(),
  })
  .strict();
export type KnowledgeRenameResponse = z.infer<typeof KnowledgeRenameResponse>;

/**
 * `NAME_TAKEN` details — the refusal as a sentence, because the office reads
 * the sentence and never the code.
 *
 * `issues` is a list of plain strings rather than `VALIDATION_FAILED`'s
 * `{path, message, code}` objects: there is no field to point at (the body has
 * one, and it is well-formed), so a path would be furniture. One entry today;
 * the array is the shape because a refusal that grows a second reason should not
 * change the key a client reads.
 */
export const NameTakenDetails = z
  .object({
    issues: z.array(z.string()),
  })
  .strict();
export type NameTakenDetails = z.infer<typeof NameTakenDetails>;

/** The sentence `NAME_TAKEN` carries, in the office's words. No id, no table. */
export const nameTakenSentence = (name: string): string =>
  `Another record already goes by "${name}".`;

/* ————————————————————————— join by hand ————————————————————————— */

/**
 * **Which of the two records the office is keeping** — the page's own, or the
 * one they just picked out of the search.
 *
 * Two words rather than an id, and the reason is what the modal shows. The
 * choice is rendered as two radios **by name**, because the office never sees an
 * id; a body carrying `keep_entity_id` would then be the client re-deriving,
 * from a name it rendered, an id it has to get exactly right — and getting it
 * wrong folds the record the reader meant to keep. `'this'` and `'other'` are
 * the two things the reader can actually point at: the record whose page they
 * are standing on, and the one in the box.
 *
 * It picks the **id and the name together**. There is no second question about
 * which name survives, because there is no case for one: the record being folded
 * away is a bare first name or a misspelling, and its name is the thing the
 * office came here to be rid of. The loser's spelling is not lost — the merge
 * leaves it standing as a redirect, which is the whole of what makes this safe.
 */
export const KNOWLEDGE_JOIN_KEEPS = ['this', 'other'] as const;
export const KnowledgeJoinKeep = z.enum(KNOWLEDGE_JOIN_KEEPS);
export type KnowledgeJoinKeep = z.infer<typeof KnowledgeJoinKeep>;

/**
 * **Join with another record** — `POST /orgs/:orgId/knowledge/:entityId/join`
 * (organisations-as-subjects note §2 chunk 1, "Join by hand").
 *
 * The by-hand door for a fold the likely-merge probe never files a question
 * about. "Clint" arriving from an import has no trigram tie to "Clint Phillips"
 * and no shared employer to notice, so nothing asks; the office knows anyway,
 * and until now their only way through was an operator at a terminal running
 * `entity:merge`.
 *
 * **People only, for now.** The endpoint refuses anything else — not because the
 * merge cannot fold an organisation (it can, and the CLI does), but because an
 * organisation's duplicates arrive through the probe, which reads names it can
 * compare, and a second by-hand door for a case that already has a card is two
 * doors to one act.
 *
 * Both fields are required. There is no partial join: a body without `keep`
 * would be asking the server to guess which of two records the office meant to
 * be rid of, which is the one thing this gesture exists to state.
 */
export const KnowledgeJoinBody = z
  .object({
    /** The other record, picked out of the knowledge base's own search. */
    other_entity_id: Uuid,
    keep: KnowledgeJoinKeep,
  })
  .strict();
export type KnowledgeJoinBody = z.infer<typeof KnowledgeJoinBody>;

/**
 * What the join came back as.
 *
 * `survivor` is the record that stands, **after** the fold — its id, so a page
 * standing on the record that just went away knows where to point itself, and
 * its name, so the sentence beside it has something to say. The id is the one
 * thing on this response that is not for reading: the office sees names.
 *
 * `sentence` is the one line the office reads, and it carries the half of a join
 * nobody can see — that the old entry still finds the record. Without that
 * clause a reader has no way to know the bare "Clint" they just folded away will
 * go on matching the next claim that says it, which is the whole reason a fold
 * is safe to do by hand.
 *
 * Nothing here is nullable: both are known by the time there is a response at
 * all.
 */
export const KnowledgeJoinResponse = z
  .object({
    survivor: z
      .object({
        entity_id: Uuid,
        display_name: z.string(),
      })
      .strict(),
    sentence: z.string(),
  })
  .strict();
export type KnowledgeJoinResponse = z.infer<typeof KnowledgeJoinResponse>;

/**
 * **What the join would do, before the office presses it**
 * (`docs/notes/2026-09-16-fold-tracked-people.md` §7) —
 * `GET /orgs/:orgId/knowledge/:entityId/join-preview?other=…&keep=this|other`.
 *
 * The modal shows two names and a radio pair, and until 2026-09-16 that was the
 * whole of what a join did: two spellings became one and the old one went on
 * resolving. A fold now carries a tracked record — it unbinds the losing side's
 * identity, supersedes its brief, moves its runs and its status — and this door
 * lets the office pick **which** of the two goes, so the consequence turns on a
 * radio button they are about to press.
 *
 * A **read**, and a GET, because it writes nothing and answers about a pair the
 * page already knows: the dry-run-as-POST shape belongs to doors whose preview
 * costs a transaction, and this one is four counts and an ordering.
 *
 * `readback` is the same server-owned sentence the merge card and the connector
 * give, from the same function, for the direction `keep` names. Null when either
 * record has gone since the page loaded — the same absence the card renders.
 */
/**
 * The preview's query string — `?other=<uuid>&keep=this|other`.
 *
 * A schema rather than two loose `@Query` strings, because both are
 * caller-supplied and both are load-bearing: `keep` decides which way round the
 * sentence is written, and a value outside the enum reaching the service would
 * be read as "not this", which is the *other* direction — a preview quietly
 * describing the wrong fold. Parsed at the door, so a bad one is a 400 with the
 * field named rather than a 500 from a zod throw inside a read.
 */
export const KnowledgeJoinPreviewQuery = z
  .object({
    other: Uuid,
    keep: KnowledgeJoinKeep,
  })
  .strict();
export type KnowledgeJoinPreviewQuery = z.infer<typeof KnowledgeJoinPreviewQuery>;

export const KnowledgeJoinPreview = z
  .object({
    readback: z.string().nullable(),
  })
  .strict();
export type KnowledgeJoinPreview = z.infer<typeof KnowledgeJoinPreview>;

/** The one line the office reads back. Two names, and the promise about the old one. */
export const knowledgeJoinedSentence = (loser: string, survivor: string): string =>
  `${loser} is now part of ${survivor}. The old entry still finds it.`;

/**
 * **The two refusals a join says in its own words**, on the path they belong to.
 *
 * Every one of them is a plain sentence with no id in it: the office is looking
 * at two names, and a message naming a uuid is a message about the inside of the
 * machine.
 */
export const KNOWLEDGE_JOIN_SAME_RECORD =
  'A record cannot be joined with itself. Pick a different one.';
export const KNOWLEDGE_JOIN_PEOPLE_ONLY = 'Only people can be joined for now.';
export const KNOWLEDGE_JOIN_KIND_MISMATCH =
  'These two are different kinds of record and cannot be joined.';

/* ————————————————————————— the whole graph ————————————————————————— */

/**
 * `GET /orgs/:orgId/knowledge-graph` — every tie the organization holds, drawn
 * at once from the knowledge base's footer.
 *
 * `account_id` is **required in a security organization** and refused in every
 * other one: accounts are walls, and a picture that put two engagements' records
 * side by side is the one thing this vertical must never show.
 */
export const KnowledgeGraphQuery = z.object({ account_id: Uuid.optional() }).strict();
export type KnowledgeGraphQuery = z.infer<typeof KnowledgeGraphQuery>;

/** One circle. Only what the tooltip says; the details pane reads the panel. */
export const KnowledgeGraphNode = z
  .object({
    id: Uuid,
    kind: NetworkNodeKind,
    name: z.string(),
    tracked: z.boolean(),
    status: SubjectStatus.nullable(),
  })
  .strict();
export type KnowledgeGraphNode = z.infer<typeof KnowledgeGraphNode>;

/** One line per pair of records, however many facts tie them. */
export const KnowledgeGraphEdge = z
  .object({
    a: Uuid,
    b: Uuid,
  })
  .strict();
export type KnowledgeGraphEdge = z.infer<typeof KnowledgeGraphEdge>;

/**
 * **How much is on record**, counted whether or not it is drawn: every live
 * person, organization and event — including the ones nothing ties to yet, which
 * the picture cannot show — and every tied pair.
 */
export const KnowledgeGraphTotals = z
  .object({
    people: z.number().int().nonnegative(),
    organizations: z.number().int().nonnegative(),
    events: z.number().int().nonnegative(),
    ties: z.number().int().nonnegative(),
  })
  .strict();
export type KnowledgeGraphTotals = z.infer<typeof KnowledgeGraphTotals>;

/**
 * The answer.
 *
 * `drawn` false means the graph is past what a browser draws in one picture
 * (`KNOWLEDGE_GRAPH_NODE_CAP` records with a tie), and `nodes` and `edges` come
 * back empty with the totals still said. `totals` is null only when the graph
 * was too large to even read inside the server's time limit.
 */
export const KnowledgeGraph = z
  .object({
    drawn: z.boolean(),
    totals: KnowledgeGraphTotals.nullable(),
    nodes: z.array(KnowledgeGraphNode),
    edges: z.array(KnowledgeGraphEdge),
  })
  .strict();
export type KnowledgeGraph = z.infer<typeof KnowledgeGraph>;

/**
 * The most records the whole-graph view draws. Past it the view gives the
 * counts. The view paints on a canvas and asks before laying out a large graph,
 * so this bounds the payload and the layout's time rather than the drawing.
 */
export const KNOWLEDGE_GRAPH_NODE_CAP = 20_000;
